import React, { useCallback, useMemo } from 'react';
import { CheckboxListItemData } from '@/experiences/sales-portal/components/searchable-checkbox-list';
import { deriveUnitsPerPack } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/utils/units-per-pack';
import { formatPriceDisplayValue } from '@/utils/money';
import {
  QuoteItemSearchResult,
  useQuoteItemSearchLoader,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-quote-item-search-loader';
import { QuoteItemSearchView } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-item-search-view';
import { QuoteLineItem } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';

type SearchResultItem = CheckboxListItemData<QuoteItemSearchResult>;

interface QuoteItemSearchProps {
  chatId: string;
  lineItems: QuoteLineItem[];
  onAddItem: (
    item: Omit<QuoteLineItem, 'currencyCode' | 'localId' | 'sortOrder'>,
  ) => void;
  onRemoveItem: (localId: string) => void;
  onSearchError?: () => void;
}

/**
 * Container for the catalog-item search combobox. Owns the data wiring:
 * runs the `searchPriceQuoteItems` loader, derives the pre-checked set from
 * the quote's existing line items, and translates a row toggle into either an
 * add (with catalog fields filled in) or a remove against the parent state.
 * Hands `results`, `isSearching`, `selectedKeys`, and the toggle handler
 * down to the presentational `QuoteItemSearchView`.
 */
export const QuoteItemSearch: React.FC<QuoteItemSearchProps> = ({
  chatId,
  lineItems,
  onAddItem,
  onRemoveItem,
  onSearchError,
}) => {
  const { hasError, isSearching, results, setQuery } = useQuoteItemSearchLoader(
    {
      chatId,
      onError: onSearchError,
    },
  );

  const selectedKeys = useMemo(
    () =>
      new Set(
        lineItems
          .map((item) => item.variantPackId)
          .filter((id): id is string => !!id),
      ),
    [lineItems],
  );

  const handleToggleItem = useCallback(
    (item: SearchResultItem) => {
      if (selectedKeys.has(item.variantPackId)) {
        const lineItem = lineItems.find(
          (li) => li.variantPackId === item.variantPackId,
        );
        if (lineItem) {
          onRemoveItem(lineItem.localId);
        }
        return;
      }
      onAddItem({
        isCatalogItemRemoved: false,
        itemCode: item.itemCode ?? undefined,
        itemDescription: item.itemDescription ?? undefined,
        itemName: item.itemName,
        itemPackSize: item.itemPackSize ?? undefined,
        ourPrice: formatPriceDisplayValue(item.packPriceInMicros),
        quantity: '1',
        unitsPerPack: deriveUnitsPerPack(
          item.packPriceInMicros,
          item.perUnitPriceInMicros,
        ),
        variantPackId: item.variantPackId,
      });
    },
    [lineItems, onAddItem, onRemoveItem, selectedKeys],
  );

  return (
    <QuoteItemSearchView
      hasError={hasError}
      isSearching={isSearching}
      onSearch={setQuery}
      onToggleItem={handleToggleItem}
      results={results}
      selectedKeys={selectedKeys}
    />
  );
};
